import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAppStore } from '@/store';
import { STAGE_LABELS, STAGE_ORDER } from '@/types';
import type { Stage } from '@/types';
import * as api from '@/api/client';
import type {
  DrawingSummary, ResetMatterDrawingsPresentDetail,
} from '@/api/client';
import {
  Lock, Check, ChevronRight, Settings, BarChart3, LogOut,
  FolderKanban, Database, RotateCcw, Loader2, Upload, Download, Share2,
  GraduationCap,
} from 'lucide-react';
import MatterAccessModal from '@/components/matter/MatterAccessModal';

/**
 * Context-budget pill — informational indicator showing estimated token
 * usage for this matter vs. the active model's context window. Green when
 * < 50%, amber when 50-80%, red when > 80%. Never a hard gate — just lets
 * the drafter see at a glance whether the model is likely to truncate.
 */
function ContextBudgetPill({ matterId }: { matterId: string }) {
  const [budget, setBudget] = useState<api.ContextBudget | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      try {
        const data = await api.getContextBudget(matterId);
        if (!cancelled) setBudget(data);
      } catch {
        // Swallow — informational only. Pill just doesn't render.
      }
    };
    fetch();
    // Re-fetch every 30s so stage transitions are picked up without
    // threading a callback through every component.
    const interval = setInterval(fetch, 30_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [matterId]);

  // Also re-fetch whenever the matter state changes (stage mutation).
  const matterState = useAppStore(s => s.matterState);
  useEffect(() => {
    if (!matterId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getContextBudget(matterId);
        if (!cancelled) setBudget(data);
      } catch { /* swallow */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterId, matterState?.matter?.updated_at]);

  if (!budget) return null;

  const { estimated_tokens, context_window, utilization_pct, model_name, bottleneck } = budget as any;
  const formatK = (n: number) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    return `${Math.round(n / 1000)}k`;
  };

  const color =
    utilization_pct >= 80 ? 'bg-red-100 text-red-800 border-red-200' :
    utilization_pct >= 50 ? 'bg-amber-100 text-amber-800 border-amber-200' :
    'bg-green-100 text-green-800 border-green-200';

  const tooltipText =
    `Estimated largest prompt: ~${formatK(estimated_tokens)} tokens of ${formatK(context_window)} ` +
    `(${model_name})` +
    (bottleneck ? `. Bottleneck: ${bottleneck}` : '') +
    '. ' +
    (utilization_pct >= 80
      ? 'Context is tight — the model may truncate. Consider splitting documents or switching to a higher-context model.'
      : utilization_pct >= 50
        ? 'Getting full. Keep an eye on this if you add more documents.'
        : 'Plenty of room.');

  return (
    <span
      data-testid="context-budget-pill"
      title={tooltipText}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-mono border cursor-default select-none ${color}`}
    >
      <BarChart3 size={10} />
      ~{formatK(estimated_tokens)} / {formatK(context_window)}
    </span>
  );
}

/**
 * PageIndex aggregate badge — green when every non-archived
 * matter_documents row is ``status='indexed'``; amber if any are still
 * in flight; red if any have failed. Polls every 10s while any row is
 * in flight, every 60s otherwise.
 */
function PageIndexBadge({ matterId }: { matterId: string }) {
  const [summary, setSummary] = useState<{ total: number; done: number; failed: number; inflight: number } | null>(null);

  useEffect(() => {
    if (!matterId) return;
    let cancelled = false;
    let tick = 0;
    const pollInterval = () => {
      if (!summary) return 10_000;
      return summary.inflight > 0 ? 10_000 : 60_000;
    };
    const load = async () => {
      try {
        const data = await api.listMatterDocuments(matterId);
        if (cancelled) return;
        const docs = (data.documents || []).filter((d) => d.status !== 'archived');
        setSummary({
          total: docs.length,
          done: docs.filter((d) => d.status === 'indexed').length,
          failed: docs.filter((d) => d.status === 'failed').length,
          inflight: docs.filter((d) => d.status !== 'indexed' && d.status !== 'failed').length,
        });
      } catch {
        // Informational only.
      }
    };
    load();
    const iv = setInterval(() => { tick += 1; load(); }, 10_000);
    return () => { cancelled = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterId]);

  if (!summary || summary.total === 0) return null;
  const { total, done, failed, inflight } = summary;

  let icon = '✓';
  let cls = 'bg-green-100 text-green-800 border-green-200';
  let label = `${done}/${total}`;
  let title = 'PageIndex: all documents indexed';
  if (failed > 0) {
    icon = '✗';
    cls = 'bg-red-100 text-red-800 border-red-200';
    title = `PageIndex: ${failed} of ${total} document(s) failed — retry from Stage 1`;
  } else if (inflight > 0) {
    icon = '⏳';
    cls = 'bg-amber-100 text-amber-800 border-amber-200';
    title = `PageIndex: ${inflight} of ${total} document(s) still processing`;
  }

  return (
    <span
      data-testid="pageindex-badge"
      title={title}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-mono border cursor-default select-none ${cls}`}
    >
      <Database size={10} /> PageIndex {icon} {label}
    </span>
  );
}


/**
 * Active-models section + settings-menu dropdown were moved to a
 * shared ``./SettingsMenu`` component so the dashboard Home page can
 * render the identical popup. This file keeps only the wrapper that
 * feeds the shared component with the current ``matterId``.
 */
import SettingsMenu from './SettingsMenu';


interface TopBarProps {
  onOpenSettings?: () => void;
  onOpenPrompts?: () => void;
  onOpenSkill?: () => void;
  onOpenDiagnostic?: () => void;
  onOpenUsers?: () => void;
  onOpenStageJobs?: () => void;
  onOpenMatters?: () => void;
  /** The Courses page (user-guide videos). */
  onOpenCourses?: () => void;
  onLogout?: () => void;
  // currentUser carries .id so the Sharing modal can detect ownership;
  // without it the modal still works (admin sees collaborators) but
  // can't show the "You're the owner" hint for drafters.
  currentUser?: { id?: number; email: string; role: string } | null;
}

export default function TopBar({ onOpenSettings, onOpenPrompts, onOpenSkill, onOpenDiagnostic, onOpenUsers, onOpenStageJobs, onOpenMatters, onOpenCourses, onLogout, currentUser }: TopBarProps) {
  const {
    matterState, activeStage, setActiveStage, setReadOnlyStage,
    setMatterState, setResetBanner,
  } = useAppStore();
  const matter = matterState?.matter;

  // Global reset flow — visible whenever any part of the matter is
  // frozen (matter.status, or a stage_state.is_frozen flag). Keeps the
  // same hard-reset semantics Stage 3's [Reset Claims] button uses so
  // the drafter can restart from anywhere (Stage 1 or Stage 2 after
  // freeze, not just Stage 3).
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  // Sharing modal — visible to anyone who can see the matter (the
  // /collaborators GET works for owner / admin / collaborator).
  // Mutation controls inside the modal are admin-gated.
  const [sharingOpen, setSharingOpen] = useState(false);
  // Phase 10 — drawings-aware reset state. The first call to
  // performReset goes without the confirm flag; on a 409
  // ``drawings_present`` response, the dialog flips to the enhanced
  // mode (summary + ZIP download + acknowledgement checkbox) and
  // the second call carries confirm=true.
  const [drawingSummary, setDrawingSummary] = useState<DrawingSummary | null>(null);
  const [drawingExportUrl, setDrawingExportUrl] = useState<string | null>(null);
  const [drawingsAcknowledged, setDrawingsAcknowledged] = useState(false);
  const stages = (matterState as any)?.stages as Record<string, any> | undefined;
  const anyStageFrozen = !!stages && Object.values(stages).some(
    (s) => s && (s as any).is_frozen,
  );
  const matterFrozen = (matter as any)?.status === 'frozen';
  const canReset = !!matter && (matterFrozen || anyStageFrozen);

  // Reset all drawings-aware fields when the dialog closes so a
  // subsequent open starts fresh (no stale acknowledged-checkbox).
  const closeResetDialog = () => {
    setResetConfirmOpen(false);
    setDrawingSummary(null);
    setDrawingExportUrl(null);
    setDrawingsAcknowledged(false);
  };

  const performReset = async () => {
    if (!matter) return;
    setResetting(true);
    try {
      // First-call body has confirm=true ONLY when the dialog has
      // already surfaced the drawings-aware variant AND the drafter
      // checked the acknowledgement. Otherwise we send no body so a
      // matter without drawings goes through the byte-identical
      // pre-Phase-10 path.
      const body = drawingSummary && drawingsAcknowledged
        ? { confirm_drawings_will_be_deleted: true }
        : undefined;
      const res = await api.resetMatter(matter.id, body);
      const fresh = await api.getMatter(matter.id);
      setMatterState(fresh);
      setResetBanner({ count: res?.deleted_claims ?? 0 });
      setReadOnlyStage(null);
      setActiveStage('stage_1');
      closeResetDialog();
    } catch (e: unknown) {
      // Phase 10 — 409 drawings_present: surface the enhanced
      // variant inline rather than closing the dialog. Any other
      // error closes the dialog (best-effort; banner won't appear).
      if (axios.isAxiosError(e) && e.response?.status === 409) {
        const detail = e.response.data?.detail as
          | ResetMatterDrawingsPresentDetail
          | undefined;
        if (detail?.error_code === 'drawings_present') {
          setDrawingSummary(detail.drawing_summary);
          setDrawingExportUrl(detail.export_zip_url);
          setDrawingsAcknowledged(false);
          setResetting(false);
          return;
        }
      }
      closeResetDialog();
    } finally {
      setResetting(false);
    }
  };

  if (!matter) return null;

  const currentIdx = STAGE_ORDER.indexOf(matter.current_stage);

  const handleStageClick = (stage: Stage) => {
    const idx = STAGE_ORDER.indexOf(stage);
    if (idx <= currentIdx) {
      // Explicit user-initiated navigation always exits read-only view.
      const from_stage = activeStage;
      setReadOnlyStage(null);
      setActiveStage(stage);
      api.postEvent({
        event_type: 'stage_navigated',
        event_category: 'navigation',
        matter_id: matter.id,
        element_id: `stage_tab_${stage}`,
        event_data: { from_stage, to_stage: stage },
      });
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-white">
    <div
      data-testid="top-bar"
      className="h-14 border-b border-zinc-200 bg-white flex items-center px-4 justify-between w-full"
    >
      <div className="flex items-center gap-4">
        <button
          data-testid="home-button"
          onClick={() => useAppStore.getState().reset()}
          className="text-sm font-semibold tracking-tight text-zinc-900 hover:text-[#002FA7] transition-colors"
          style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
        >
          Draft Studio
        </button>
        <span className="text-zinc-300">|</span>
        <span data-testid="matter-title" className="text-sm font-medium text-zinc-700 truncate max-w-[200px]">
          {matter.title}
        </span>
        <span
          data-testid="jurisdiction-badge"
          className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-zinc-100 text-zinc-800 border border-zinc-200"
        >
          {matter.jurisdiction}
          <Lock size={10} className="text-zinc-500" />
        </span>
        {/* Sharing button — opens the collaborator modal. Visible to
            anyone who can see the matter; mutation controls inside
            the modal are admin-only. */}
        <button
          type="button"
          data-testid="matter-sharing-button"
          onClick={() => setSharingOpen(true)}
          title="Sharing"
          aria-label="Open matter sharing"
          className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-[#002FA7] transition-colors px-1.5 py-0.5 rounded-sm hover:bg-zinc-100"
        >
          <Share2 size={12} />
          <span className="hidden sm:inline">Sharing</span>
        </button>
      </div>

      <div className="flex items-center gap-1" data-testid="stage-progress">
        {/* Stage 0 → 1 → 2 → 3 → 4. Stage 2.5 is merged into Stage 2
            as the Strategy tab so it's filtered out of the visible
            row while still being a legal activeStage value. Stage 0
            (Upload) is always accessible — it precedes every other
            stage in STAGE_ORDER, so isAccessible (idx ≤ currentIdx)
            is true for every matter regardless of how far along it
            is. That preserves the "go back to upload supplementary
            docs" affordance the spec calls out.
            Highlighting Stage 2 as current when activeStage is
            stage_2_5 keeps the visual in sync with the merged UI. */}
        {STAGE_ORDER.filter(s => s !== 'stage_2_5').map((stage, i, arr) => {
          const idx = STAGE_ORDER.indexOf(stage);
          const currentMappedToStage2 = activeStage === 'stage_2_5' ? 'stage_2' : activeStage;
          const isCurrent = stage === currentMappedToStage2;
          const isCompleted = idx < currentIdx;
          const isAccessible = idx <= currentIdx;
          // Prev-stage instruction surfaced as a tooltip on any
          // locked tab so the drafter sees why it's disabled and
          // what to do next. ``stage_2_5`` is filtered out above,
          // so the prev-of-stage_3 lookup lands on stage_2 cleanly.
          const prevStage = STAGE_ORDER[idx - 1];
          const disabledTitle = !isAccessible
            ? (stage === 'stage_2'
                ? 'Complete and freeze Stage 1 first'
                : `Complete ${STAGE_LABELS[prevStage as Stage] || prevStage} first`)
            : undefined;
          // Display label: Stage 0 wants the icon + "Upload" wording
          // rather than the bare stage number. Other stages keep
          // their existing "{n} {long label on lg+}" pattern.
          const isStage0 = stage === 'stage_0';

          return (
            <React.Fragment key={stage}>
              {i > 0 && <ChevronRight size={14} className="text-zinc-300 mx-0.5" />}
              <button
                data-testid={`stage-nav-${stage}`}
                onClick={() => handleStageClick(stage)}
                disabled={!isAccessible}
                title={disabledTitle}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-medium transition-colors ${
                  isCurrent
                    ? 'bg-[#002FA7] text-white'
                    : isCompleted
                    ? 'bg-zinc-100 text-zinc-900 hover:bg-zinc-200 cursor-pointer'
                    : 'text-zinc-400 cursor-not-allowed'
                }`}
              >
                {isCompleted && <Check size={10} />}
                {isStage0 && !isCompleted && <Upload size={11} />}
                {isStage0 ? '0' : stage.replace('stage_', '')}
                <span className="hidden lg:inline">{STAGE_LABELS[stage]}</span>
              </button>
            </React.Fragment>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <ContextBudgetPill matterId={matter.id} />
        <PageIndexBadge matterId={matter.id} />
        {/* Models moved into the Settings dropdown (Active Models
            section) — the standalone popover used to sit here but
            was the last piece of clutter in the actions row. */}
        {onOpenMatters && (
          <button
            data-testid="topbar-matters-button"
            onClick={onOpenMatters}
            title="Matters list"
            className="text-zinc-500 hover:text-zinc-900 transition-colors p-1.5 flex items-center gap-1 text-xs"
          >
            <FolderKanban size={16} /> Matters
          </button>
        )}
        {onOpenCourses && (
          <button
            type="button"
            data-testid="topbar-courses-button"
            onClick={onOpenCourses}
            title="User-guide videos"
            className="text-zinc-500 hover:text-zinc-900 transition-colors p-1.5 flex items-center gap-1 text-xs"
          >
            <GraduationCap size={16} /> Courses
          </button>
        )}
        {canReset && (
          <button
            data-testid="topbar-reset-matter-button"
            onClick={() => setResetConfirmOpen(true)}
            title="Reset matter — unfreeze every stage, delete claims, start over"
            className="text-red-600 hover:text-red-800 transition-colors p-1.5 inline-flex items-center gap-1 text-xs"
          >
            <RotateCcw size={14} /> Reset
          </button>
        )}
        {/* Consolidated Settings menu. Active Models (read-only),
            plus Settings / Prompts / Skill Studio / Diagnostic /
            Users. Every admin entry is gated in App.js by isAdmin:
            non-admin users (drafter, reviewer) receive undefined
            for each onOpen* prop, and SettingsMenu only renders a
            menu item when its prop is truthy — so drafters see
            the gear → Active Models section → nothing else.
            currentUserRole is threaded through as a belt-and-braces
            filter: if a future caller forgets to gate its props,
            SettingsMenu still drops the admin items for non-admins. */}
        <SettingsMenu
          matterId={matter?.id || null}
          currentUserRole={currentUser?.role || null}
          onOpenSettings={onOpenSettings}
          onOpenPrompts={onOpenPrompts}
          onOpenSkill={onOpenSkill}
          onOpenDiagnostic={onOpenDiagnostic}
          onOpenUsers={onOpenUsers}
          onOpenStageJobs={onOpenStageJobs}
        />
        {onLogout && (
          <button
            data-testid="topbar-logout-button"
            onClick={onLogout}
            title={currentUser?.email ? `Sign out ${currentUser.email}` : 'Sign out'}
            className="text-zinc-500 hover:text-zinc-900 transition-colors p-1.5 flex items-center gap-1 text-xs"
          >
            <LogOut size={16} />
          </button>
        )}
      </div>
    </div>
    {/* Second-row Models strip removed — it's now a popover button
        inside the actions area of the main top bar. Saves a row of
        vertical space for the stage content. */}
    {/* Reset confirmation — shared dialog for the global [Reset]
        action. Same copy/flow as Stage 3's [Reset Claims] but fired
        from the TopBar so Stage 1 / 2 drafters can start over
        without having to navigate to Stage 3 first. */}
    {resetConfirmOpen && (
      <div
        data-testid="topbar-reset-modal"
        className="fixed inset-0 z-[60] flex items-center justify-center bg-zinc-900/40 backdrop-blur-sm p-6"
        onClick={() => !resetting && closeResetDialog()}
      >
        <div
          className="w-full max-w-md bg-white rounded-sm border border-zinc-200 shadow-xl p-5"
          onClick={(e) => e.stopPropagation()}
          data-testid={drawingSummary ? "topbar-reset-modal-drawings" : "topbar-reset-modal-default"}
        >
          <h3
            className="text-base font-semibold text-zinc-900 mb-2"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
          >
            {drawingSummary
              ? 'Reset matter — drawings will be deleted'
              : 'Reset matter and start over?'}
          </h3>
          {drawingSummary ? (
            <>
              <p className="text-xs text-zinc-600 leading-relaxed mb-3">
                This will delete every claim, every Stage 2/3 analysis,
                AND all drawing-side state for this matter. Your
                uploaded documents and PageIndex trees are preserved.
              </p>
              <div
                data-testid="topbar-reset-drawings-summary"
                className="text-xs text-zinc-700 leading-relaxed mb-3 rounded-sm border border-zinc-200 bg-zinc-50 px-3 py-2"
              >
                <strong>{drawingSummary.figures_count}</strong>
                {' figure'}
                {drawingSummary.figures_count === 1 ? '' : 's'}
                {drawingSummary.frozen_count > 0 &&
                  <> · <strong>{drawingSummary.frozen_count}</strong> frozen</>}
                {drawingSummary.unanalyzed_count > 0 &&
                  <> · <strong>{drawingSummary.unanalyzed_count}</strong> unanalysed</>}
                {drawingSummary.issues_count > 0 &&
                  <> · <strong className="text-amber-700">
                    {drawingSummary.issues_count}
                  </strong> verify issue{drawingSummary.issues_count === 1 ? '' : 's'}</>}
              </div>
              {drawingExportUrl && (
                <a
                  href={drawingExportUrl}
                  data-testid="topbar-reset-drawings-download"
                  className="inline-flex items-center gap-1.5 mb-3 px-3 py-1.5 rounded-sm text-xs font-medium border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50"
                >
                  <Download size={12} />
                  Download drawings as ZIP →
                </a>
              )}
              <label
                className="flex items-start gap-2 mb-4 text-xs text-zinc-700 cursor-pointer"
                data-testid="topbar-reset-drawings-acknowledge"
              >
                <input
                  type="checkbox"
                  checked={drawingsAcknowledged}
                  onChange={(e) => setDrawingsAcknowledged(e.target.checked)}
                  disabled={resetting}
                  className="mt-0.5"
                />
                <span>
                  I have downloaded my drawings (or don't need them).
                </span>
              </label>
              <p className="text-xs text-red-700 font-medium mb-4">
                This cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeResetDialog}
                  disabled={resetting}
                  className="px-3 py-1.5 rounded-sm text-xs border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-testid="topbar-reset-confirm-drawings"
                  onClick={performReset}
                  disabled={resetting || !drawingsAcknowledged}
                  className="px-3 py-1.5 rounded-sm text-xs font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  {resetting && <Loader2 size={12} className="animate-spin" />}
                  Reset and delete drawings
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-zinc-600 leading-relaxed mb-3">
                This unfreezes every stage, deletes all claims and strategy
                analysis, and returns you to Stage 1. Your uploaded documents
                and PageIndex trees are kept so you can start drafting
                again immediately.
              </p>
              <p className="text-xs text-red-700 font-medium mb-4">
                This cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeResetDialog}
                  disabled={resetting}
                  className="px-3 py-1.5 rounded-sm text-xs border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-testid="topbar-reset-confirm"
                  onClick={performReset}
                  disabled={resetting}
                  className="px-3 py-1.5 rounded-sm text-xs font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  {resetting && <Loader2 size={12} className="animate-spin" />}
                  Delete and Start Fresh
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    )}

    {/* Sharing modal — only mounts when matter is loaded so a stale
        ID can't slip through. Admin gets add/remove; everyone with
        access sees the list. */}
    {matter && (
      <MatterAccessModal
        open={sharingOpen}
        matterId={matter.id}
        ownerId={(matter as any).owner_id ?? null}
        currentUser={currentUser ? {
          id: currentUser.id ?? -1,
          email: currentUser.email,
          role: currentUser.role,
        } : null}
        onClose={() => setSharingOpen(false)}
      />
    )}
    </div>
  );
}
